<template>
  <v-card
    class="elevation-1"
    :loading="groupLoading || loading"
    :dark="$vuetify.theme.dark"
  >
    <v-card-title>
      <v-list-item>
        <v-list-item-avatar size="60" color="grey">
          <v-img
            class="elevation-6"
            :src="`${filesUrl}/${profilePhoto.id}?access_token=${accessToken}`"
            v-if="profilePhoto && profilePhoto.id"
          >
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular
                  indeterminate
                  color="accent"
                ></v-progress-circular>
              </v-row>
            </template>
          </v-img>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title class="headline">{{
            group.title
          }}</v-list-item-title>
          <v-list-item-subtitle v-if="group.type">{{
            group.type[0].toUpperCase() + group.type.slice(1)
          }}</v-list-item-subtitle>
          <v-btn
            class="group-delete"
            color="error"
            icon
            @click="confirmDelete = true"
            title="Delete"
          >
            <v-icon color>mdi-delete</v-icon>
          </v-btn>
          <v-btn
            class="group-refresh"
            color="primary"
            icon
            @click="refresh()"
            title="Refresh"
          >
            <v-icon color>mdi-refresh</v-icon>
          </v-btn>
        </v-list-item-content>
      </v-list-item>
    </v-card-title>

    <v-card-actions v-if="group.in_group">
      <v-btn
        color="primary"
        text
        @click="showConfig = !showConfig"
      >
        Configure
      </v-btn>

      <v-spacer></v-spacer>

      <v-list-item>
        <v-row align="center" justify="end">
          <v-icon class="mr-1" title="Member count">mdi-account-circle</v-icon>
          <span class="subheading mr-2">{{ group.member_count }}</span>
          <span class="mr-1">·</span>
          <v-icon class="mr-1" title="Administrator count"
            >mdi-shield-account</v-icon
          >
          <span class="subheading">{{ group.administrators.length }}</span>
        </v-row>
      </v-list-item>
    </v-card-actions>

    <v-expand-transition>
      <div v-show="showConfig">
        <v-card-text class="title">
          <v-alert
            type="info"
            border="left"
            class="mt-4"
            dismissible
            prominent
            dense
            v-if="!groupLoading && isNonAdminGroup"
          >
            <v-row align="center">
              <v-col class="grow"
                >Your bot is <b>not set as an administrator</b> in this group. Once set to an administrator you can refresh your
                groups using the refresh button <v-icon>mdi-refresh</v-icon>.</v-col
              >
              <v-col class="shrink">
                <v-btn @click.stop="refresh()">Refresh</v-btn>
              </v-col>
            </v-row>
          </v-alert>
          <Confirm
            :confirm.sync="confirmDelete"
            :callback="deleteGroup"
            :message="`Are you sure you want to <b>delete ${group.title}</b> group? If yes, make sure to remove the bot from the group.`"
          ></Confirm>
          <v-banner
            two-line
            :dark="$vuetify.theme.dark"
            :light="!$vuetify.theme.dark"
          >
            <v-icon slot="icon" color="warning" size="36" v-if="!isBotAdmin"
              >mdi-alert</v-icon
            >
            <v-icon slot="icon" color="green" size="36" v-if="isBotAdmin"
              >mdi-check-circle</v-icon
            >
            <p>
              <span class="font-weight-light">Bot group member status</span>
              : {{ group.in_group ? group.permissions.status : "removed" }}
            </p>
            <p
              v-if="group.in_group && !isBotAdmin"
              class="subtitle-2 font-weight-light"
            >
              Bot will not be able to manage group members and registration for this
              group. Please make the bot an administrator on this group.
            </p>
            <p
              v-if="group.in_group && isBotAdmin"
              class="subtitle-2 font-weight-light"
            >
              This bot is configured properly and is an administrator on this group.
            </p>
            <p v-if="!group.in_group" class="subtitle-2 font-weight-light">
              This bot was removed from this group.
            </p>
          </v-banner>

          <v-alert
            type="warning"
            border="left"
            dismissible
            v-if="group.in_group && isBotAdmin && !groupHasRestrictions"
          >
            Currently, no registered member will be invited or allowed to join this
            group.
            <br />
            <small
              >If this is not the intended outcome, use the options below to invite
              newly registered members to this group.</small
            >
          </v-alert>

          <v-banner
            two-line
            transition="slide-y-transition"
            v-if="group.in_group && isBotAdmin"
          >
            <v-icon
              slot="icon"
              :color="group.bot_managed ? 'green' : 'warning'"
              size="36"
              >mdi-robot</v-icon
            >
            <p>Group Management</p>
            <p v-if="group.bot_managed" class="subtitle-2 font-weight-light">
              This group will not allow non-registered members. Users that try to
              join the group will be removed from the group by the bot if they're
              not registered.
            </p>
            <p v-if="!group.bot_managed" class="subtitle-2 font-weight-light">
              Anyone with a group invite link will be able to join the group. Group
              can not be used as a trigger group for actions.
            </p>
            <template v-slot:actions>
              <v-switch
                v-model="group.bot_managed"
                @change="saveGroup()"
                inset
                class="px-4 mt-4"
                label="Bot Managed"
              ></v-switch>
            </template>
          </v-banner>
          <v-banner
            two-line
            transition="slide-y-transition"
            v-if="group.in_group && group.bot_managed"
          >
            <v-icon
              slot="icon"
              :color="group.invite ? 'green' : 'warning'"
              size="36"
              >mdi-account-group</v-icon
            >
            <p>Group Invitation</p>
            <p v-if="group.invite" class="subtitle-2 font-weight-light">
              Invite all registered members to this group. Keep in mind that anyone
              can register with your bot.
            </p>
            <p v-if="group.properties.which_members === 'white_listed_members'" class="text-body-2 font-weight-thin">
              (Members can be whitelisted in the members table using the member view.)
            </p>
            <p v-if="!group.invite" class="subtitle-2 font-weight-light">
              Do not invite all registered members to this group. Only members in
              the geofence/s and / or restrict file will be invited / allowed.
            </p>
            <template v-slot:actions>
              <div>
                <v-switch
                  v-model="group.invite"
                  @change="saveGroup()"
                  inset
                  class="px-4 mt-4"
                  label="Invite Members"
                ></v-switch>
                <v-radio-group
                  v-if="group.invite"
                  v-model="group.properties.which_members"
                  class="px-4"
                  mandatory
                  @change="saveGroup()"
                >
                  <v-radio
                    label="All Members"
                    value="all_members"
                  ></v-radio>
                  <v-radio
                    label="Verified Members"
                    value="verified_members"
                  ></v-radio>
                  <v-radio
                    label="White Listed Members"
                    value="white_listed_members"
                  ></v-radio>
                </v-radio-group>
              </div>
            </template>
          </v-banner>

          <!-- <v-banner
            two-line
            transition="slide-y-transition"
            v-if="group.in_group && isBotAdmin"
          >
            <v-icon
              slot="icon"
              :color="group.bot_managed ? 'green' : 'warning'"
              size="36"
              >mdi-robot</v-icon
            >
            <p>Group Permissions</p>
            <p class="subtitle-2 font-weight-light">
              Member permissions for this group.
            </p>
            <template v-slot:actions>
              <v-switch
                v-model="group.properties.only_verified_members_send_messages"
                @change="saveGroup()"
                inset
                class="px-4 mt-4"
                label="Only Verified Members Can Send Messages"
              ></v-switch>
            </template>
          </v-banner> -->

          <v-row v-if="group.in_group && group.bot_managed && !group.invite">
            <v-col cols="12">
              <p class="text--primary">Restrictions</p>
              <p class="body-1">
                If both geofencing and a restrict file are used, you can choose to
                either require both to be valid for the member or just one.
              </p>
            </v-col>
            <v-col cols="12" md="5">
              <v-card outlined>
                <v-card-title>
                  <v-icon large left>mdi-map-marker-check</v-icon>
                  <span class="title font-weight-light">Geofence</span>
                </v-card-title>
                <v-card-text>
                  <p>
                    Link group to a geofence. Only members inside the chosen
                    geofence/s will be invited (and thus allowed) to join this
                    group.
                  </p>
                  <v-select
                    v-model="group.geofences"
                    :items="geofences"
                    @change="saveGroup()"
                    return-object
                    multiple
                    item-text="name"
                  >
                    <template v-slot:selection="{ item, index }">
                      <v-chip
                        v-if="index < 2"
                        :color="item.options.color"
                        class="font-weight-bold"
                      >
                        <span>{{ item.name }}</span>
                      </v-chip>
                      <span v-if="index === 2" class="grey--text caption"
                        >(+{{ group.geofences.length - 2 }} others)</span
                      >
                    </template>
                  </v-select>

                  <v-radio-group
                    v-model="group.properties.which_members"
                    class="px-1"
                    label="Member Verified Status"
                    mandatory
                    @change="saveGroup()"
                  >
                    <v-radio
                      label="All Members"
                      value="all_members"
                    ></v-radio>
                    <v-radio
                      label="Verified Members"
                      value="verified_members"
                    ></v-radio>
                  </v-radio-group>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col
              cols="12"
              md="2"
              align-self="center"
              class="d-flex justify-center"
            >
              <v-radio-group
                v-model="group.properties.restrictOp"
                mandatory
                @change="saveGroup()"
                v-if="
                  group.geofences &&
                  group.geofences.length &&
                  restrictFile &&
                  restrictFile.id
                "
              >
                <v-radio label="OR" value="or"></v-radio>
                <v-radio label="AND" value="and"></v-radio>
              </v-radio-group>
            </v-col>
            <v-col cols="12" md="5">
              <Upload
                icon="mdi-clipboard-list"
                icon-color
                label="Whitelist File"
                title="Whitelist"
                description="Restrict member registrations to a pre-defined list. Those not in this whitelist will not be able to join this group."
                :multiple="false"
                :id="restrictFile.id"
                :data="{ for: 'registrationRestriction' }"
                :actions="['remove', 'download']"
                :uploaded="fileUploaded"
                :removed="deletedRestrictFile"
                :clearable="false"
              ></Upload>
              <a class="body-2" href="/GroupBot Whitelist.csv"
                >Whitelist Template File</a
              >
            </v-col>
          </v-row>
        </v-card-text>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import { models } from "feathers-vuex";
import Upload from "../widget/Upload";
import Confirm from "@/components/widget/Confirm";

export default {
  data() {
    return {
      loading: false,
      confirmDelete: false,
      refreshGroup: false,
      showConfig: false,
    };
  },
  created() {
    if (!this.group.properties.restrictOp)
      this.group.properties.restrictOp = "or";
  },
  computed: {
    ...mapGetters("config", ["botApiUrl"]),
    ...mapState("apiAuth", ["accessToken"]),
    filesUrl() {
      return new URL(
        process.env.VUE_APP_FILES_PATH,
        this.botApiUrl || process.env.VUE_APP_API_URL
      ).toString();
    },
    groupLoading() {
      return this.isGetGroupGetPending;
    },
    isBotAdmin() {
      return (
        this.group.permissions &&
        this.group.permissions.status === "administrator"
      );
    },
    isNonAdminGroup() {
      return (
        this.group.permissions &&
        this.group.permissions.status !== "administrator"
      );
    },
    profilePhoto() {
      return (
        (this.group.files &&
          this.group.files.find((file) => file.for === "profilePhoto")) ||
        {}
      );
    },
    restrictFile() {
      return (
        (this.group.files &&
          this.group.files.find(
            (file) => file.for === "registrationRestriction"
          )) ||
        {}
      );
    },
    groupHasRestrictions() {
      let restrict_file =
        this.group.files &&
        this.group.files.find((file) => file.for === "registrationRestriction");
      return (
        this.group.bot_managed &&
        (this.group.invite ||
          (this.group.geofences && this.group.geofences.length) ||
          (restrict_file && restrict_file.key))
      );
    },
  },
  methods: {
    ...mapActions("app", ["showMessage"]),
    async fileUploaded(file) {
      await this.group.patch({ data: { files: file } });
    },
    deletedRestrictFile(file) {
      for (var i = 0; i < this.group.files.length; i++) {
        if (this.group.files[i].id === file.id) {
          this.group.files.splice(i, 1);
          break;
        }
      }
    },
    async refresh() {
      const { Group } = models.bot;
      this.groupLoading = true;
      this.group = await Group.get(this.group.id, {
        query: {
          options: {
            update: {
              telegram: true,
            },
          },
        },
      });
      this.groupLoading = false;
    },
    async saveGroup() {
      this.loading = true;
      try {
        await this.group.save();
        this.loading = false;
      } catch (error) {
        this.loading = false;
      }
    },
    close() {
      this.$emit("update:group", null);
      this.$emit("on-close");
    },
    async deleteGroup() {
      try {
        this.group.deleted = true;
        await this.group.save();
        this.$emit("update:removed", true);
        this.close();
      } catch (error) {
        this.showMessage({
          text: error.message || "Error occured!",
          color: "error",
          position: "top",
          timeout: 7000,
        });
      }
    },
  },
  props: {
    group: {
      type: Object,
    },
    geofences: {
      type: Array,
      default: () => {
        return [];
      },
    },
  },
  components: {
    Upload,
    Confirm,
  },
};
</script>

<style lang="scss" scoped>
.group-delete {
  position: absolute;
  right: 0;
}
.group-refresh {
  position: absolute;
  right: 2rem;
}
</style>